import api from '../services/api';

const PENDING_QUEUE_KEY = 'biodentality_offline_queue';
const SYNC_LOCK_KEY = 'biodentality_offline_sync_lock';
const SYNC_LOCK_TTL_MS = 2 * 60 * 1000;
const SYNC_TIMEOUT_MS = 60000;

// Evento que se emite cada vez que cambia la cola (para el indicador visual)
export const QUEUE_CHANGED_EVENT = 'biodentality-offline-queue-changed';

const notifyQueueChanged = () => {
    try {
        window.dispatchEvent(new Event(QUEUE_CHANGED_EVENT));
    } catch (e) { /* noop */ }
};

const writeQueue = (queue) => {
    localStorage.setItem(PENDING_QUEUE_KEY, JSON.stringify(queue));
    notifyQueueChanged();
};

// Pide al navegador que no borre el almacenamiento local por falta de espacio (tablets/celulares)
const requestPersistentStorage = () => {
    try {
        if (navigator.storage && navigator.storage.persist) {
            navigator.storage.persist().catch(() => {});
        }
    } catch (e) { /* noop */ }
};

export const savePendingTransaction = (type, data) => {
    try {
        const queue = getPendingTransactions();
        const transaction = {
            id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
            type,
            data,
            timestamp: new Date().toISOString(),
            attempts: 0,
            lastError: null,
            // Progreso parcial: evita crear el paciente dos veces si el registro clínico falla después
            progress: {}
        };
        queue.push(transaction);
        writeQueue(queue);
        requestPersistentStorage();
        return true;
    } catch (err) {
        console.error('Error saving to offline queue:', err);
        return false;
    }
};

export const getPendingTransactions = () => {
    try {
        const data = localStorage.getItem(PENDING_QUEUE_KEY);
        return data ? JSON.parse(data) : [];
    } catch (err) {
        return [];
    }
};

export const removePendingTransaction = (id) => {
    try {
        const queue = getPendingTransactions();
        writeQueue(queue.filter(item => item.id !== id));
    } catch (err) {
        console.error('Error removing from queue:', err);
    }
};

const updatePendingTransaction = (id, changes) => {
    const queue = getPendingTransactions();
    const idx = queue.findIndex(item => item.id === id);
    if (idx === -1) return null;
    queue[idx] = { ...queue[idx], ...changes };
    writeQueue(queue);
    return queue[idx];
};

// Descarga una copia de la cola como archivo JSON (respaldo manual antes de cualquier acción)
export const exportPendingTransactions = () => {
    const queue = getPendingTransactions();
    const blob = new Blob([JSON.stringify(queue, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `biodentality-pendientes-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

const describeError = (err) => {
    const status = err?.response?.status;
    const data = err?.response?.data;
    if (!status) return `Sin conexión con el servidor (${err?.message || 'error de red'})`;
    return `HTTP ${status}: ${data ? JSON.stringify(data) : err.message}`;
};

// Errores que deben detener la sincronización y reintentarse más tarde, sin tocar la cola
const isRetryLaterError = (err) => {
    const status = err?.response?.status;
    return !status || status === 401 || status === 403 || status === 408 || status === 429 || status >= 500;
};

const acquireLock = () => {
    try {
        const current = parseInt(localStorage.getItem(SYNC_LOCK_KEY) || '0', 10);
        if (current && Date.now() - current < SYNC_LOCK_TTL_MS) return false;
        localStorage.setItem(SYNC_LOCK_KEY, String(Date.now()));
        return true;
    } catch (e) {
        return true;
    }
};

const releaseLock = () => {
    try { localStorage.removeItem(SYNC_LOCK_KEY); } catch (e) { /* noop */ }
};

const isCedulaConflict = (err) => err?.response?.status === 400 && !!err.response.data?.cedula;

const findPatientByCedula = async (cedula) => {
    if (!cedula) return null;
    const res = await api.get(`/api/patients/?search=${encodeURIComponent(cedula)}`, { timeout: SYNC_TIMEOUT_MS });
    const results = res.data?.results || res.data || [];
    return results.find(p => String(p.cedula) === String(cedula)) || null;
};

// Paso 1: asegurar que exista el paciente en el servidor. Guarda el id en `progress` apenas lo obtiene.
const ensurePatient = async (item) => {
    const { patientData } = item.data;
    let patientId = item.progress?.patientId || item.data.patientId || null;

    if (patientId) {
        if (patientData && !item.progress?.patientSaved) {
            await api.patch(`/api/patients/${patientId}/`, patientData, { timeout: SYNC_TIMEOUT_MS });
        }
    } else if (patientData) {
        try {
            const res = await api.post('/api/patients/', patientData, { timeout: SYNC_TIMEOUT_MS });
            patientId = res.data.id;
        } catch (err) {
            // La cédula ya existe: usar ese paciente en lugar de perder la historia
            if (!isCedulaConflict(err)) throw err;
            const existing = await findPatientByCedula(patientData.cedula);
            if (!existing) throw err;
            patientId = existing.id;
            await api.patch(`/api/patients/${patientId}/`, patientData, { timeout: SYNC_TIMEOUT_MS });
        }
    }

    if (patientId) {
        updatePendingTransaction(item.id, { progress: { ...(item.progress || {}), patientId, patientSaved: true } });
    }
    return patientId;
};

const syncTransaction = async (item) => {
    const data = item.data || {};

    if (item.type === 'DELETE_QUOTE') {
        try {
            await api.delete(`/api/clinical-records/${data.quoteId}/`, { timeout: SYNC_TIMEOUT_MS });
        } catch (err) {
            if (err?.response?.status !== 404) throw err; // ya no existe: se considera hecho
        }
        return;
    }

    if (!['NEW_QUOTE_OR_CLINICAL_RECORD', 'NEW_QUOTE', 'UPDATE_QUOTE'].includes(item.type)) {
        throw new Error(`Tipo de transacción desconocido: ${item.type}`);
    }

    const patientId = await ensurePatient(item);
    if (!patientId) {
        throw new Error('No se pudo determinar el paciente de esta historia clínica');
    }

    const recordData = { ...(data.recordData || {}), patient: patientId };
    const recordId = item.progress?.recordId || data.existingQuoteId || data.quoteId || null;

    if (recordId) {
        await api.patch(`/api/clinical-records/${recordId}/`, recordData, { timeout: SYNC_TIMEOUT_MS });
    } else {
        await api.post('/api/clinical-records/', recordData, { timeout: SYNC_TIMEOUT_MS });
    }
};

/**
 * Envía al servidor todo lo guardado localmente.
 * Regla principal: una transacción SOLO se elimina de este dispositivo cuando el servidor confirmó que la guardó.
 * Si el servidor rechaza los datos, la transacción queda marcada con el error para revisarla, nunca se borra sola.
 */
export const syncAllPending = async () => {
    if (!navigator.onLine) return;
    if (!localStorage.getItem('token')) return; // sin sesión: esperar a que el usuario inicie sesión

    if (getPendingTransactions().length === 0) return;
    if (window.isSyncing || !acquireLock()) return;
    window.isSyncing = true;

    try {
        const ids = getPendingTransactions().map(item => item.id);
        console.log(`Iniciando sincronización de ${ids.length} transacciones pendientes...`);

        for (const id of ids) {
            // Releer en cada vuelta: el progreso parcial se guarda dentro de la cola
            const item = getPendingTransactions().find(t => t.id === id);
            if (!item) continue;

            try {
                await syncTransaction(item);
                removePendingTransaction(item.id);
                console.log(`Transacción ${item.id} sincronizada con éxito.`);
            } catch (err) {
                const lastError = describeError(err);
                console.error(`Fallo al sincronizar transacción ${item.id}:`, lastError);
                updatePendingTransaction(item.id, {
                    attempts: (item.attempts || 0) + 1,
                    lastError,
                    lastAttemptAt: new Date().toISOString()
                });
                // Sin red, sesión vencida o error del servidor: parar y reintentar luego
                if (isRetryLaterError(err)) break;
            }
        }
    } finally {
        window.isSyncing = false;
        releaseLock();
        notifyQueueChanged();
    }
};
